import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "@phosphor-icons/react";
import { cn } from "@/lib/cn";
import { IconButton } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { ModalProps, ModalSize } from "./Modal.types";

const sizeClassName: Record<ModalSize, string> = {
  sm: "bfrs-max-w-sm",
  md: "bfrs-max-w-lg",
  lg: "bfrs-max-w-2xl",
  xl: "bfrs-max-w-4xl",
  full: "bfrs-max-w-[calc(100vw-2rem)]"
};

export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  title,
  description,
  children,
  footer,
  size = "md",
  closeButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  contentClassName,
  headerClassName,
  footerClassName
}: ModalProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <RadixDialog.Overlay className="bfrs-fixed bfrs-inset-0 bfrs-z-50 bfrs-bg-text-primary/40 bfrs-backdrop-blur-[1px] bfrs-animate-overlay-in" />
          <RadixDialog.Content
            className={cn(
              "bfrs-fixed bfrs-left-1/2 bfrs-top-1/2 bfrs-z-50 bfrs-flex bfrs-max-h-[calc(100dvh-2rem)] bfrs-w-[calc(100%-2rem)] -bfrs-translate-x-1/2 -bfrs-translate-y-1/2 bfrs-flex-col bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-overlay bfrs-outline-none bfrs-animate-modal-content-in",
              sizeClassName[size],
              className,
              contentClassName
            )}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            {(title || description || closeButton) && (
              <div className={cn("bfrs-flex bfrs-items-start bfrs-gap-4 bfrs-border-b bfrs-border-border bfrs-px-5 bfrs-py-4", headerClassName)}>
                <div className="bfrs-min-w-0 bfrs-flex-1">
                  {title && <RadixDialog.Title className="bfrs-text-base bfrs-font-bold bfrs-text-text-primary">{title}</RadixDialog.Title>}
                  {description && <RadixDialog.Description className="bfrs-mt-1 bfrs-text-sm bfrs-leading-5 bfrs-text-text-muted">{description}</RadixDialog.Description>}
                </div>
                {closeButton && (
                  <RadixDialog.Close asChild>
                    <IconButton aria-label="Close modal" icon={<X size={16} />} variant="outline" size="sm" disabled={preventClose} />
                  </RadixDialog.Close>
                )}
              </div>
            )}
            <div className="bfrs-min-h-0 bfrs-overflow-auto bfrs-px-5 bfrs-py-4">{children}</div>
            {footer && <div className={cn("bfrs-flex bfrs-flex-col-reverse bfrs-gap-2 bfrs-border-t bfrs-border-border bfrs-bg-surface-muted/60 bfrs-px-5 bfrs-py-4 sm:bfrs-flex-row sm:bfrs-justify-end", footerClassName)}>{footer}</div>}
          </RadixDialog.Content>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
